import classNames from 'classnames';
import * as inflection from 'inflection';
import type { AnimationDefinition } from 'motion/react';
import { motion } from 'motion/react';
import React, { useEffect, useRef, useState } from 'react';
import ClickOutsideHandler from '../../utils/ClickOutsideHandler';
import { useQuillReadOnly } from '../QuillContext';
import useBlotHighlightedClass from '../controls/toolbar/useBlotHighlightedClass';
import type Attachment from '../formats/Attachment';
import type { AttachmentFile } from '../formats/Attachment';
import useElementSizeSelector from '../utils/useElementSizeSelector';
import * as styles from './AttachmentContainer.module.scss';
import AttachmentItem from './AttachmentItem';
import { useDownloadAttachments } from './DownloadAttachmentsProvider';

const COLLAPSED_HEIGHT = parseInt(styles.collapsedHeight, 10);
const BORDER_WIDTH = 1;

function useGridHeight(
  gridRef: React.RefObject<HTMLDivElement | null>,
  expanded: boolean,
) {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const gridEl = gridRef.current;
    if (!gridEl) return undefined;

    const updateHeight = () => {
      const { bottom, top } = gridEl.getBoundingClientRect();

      // this will give us either the height from top to the footer
      // or the height from top to bottom of the grid
      const preferredMaxHeight = bottom - top;

      // if the footer is too close, the attachment area should have a minimum height
      // since it would get too small otherwise. We use offsetHeight here because the
      // content could be scaled and offsetHeight will give us the non-scaled value
      const minHeight = Math.min(3 * COLLAPSED_HEIGHT, gridEl.offsetHeight);
      const actualHeight = Math.max(minHeight, preferredMaxHeight);
      setHeight(actualHeight);
    };

    if (!expanded) {
      updateHeight();
      return undefined;
    }

    const observer = new ResizeObserver(updateHeight);
    observer.observe(gridEl);

    return () => {
      observer.disconnect();
    };
  }, [gridRef, expanded]);

  return height;
}

interface AttachmentGridProps {
  children: React.ReactNode;
  count: number;
  narrow: boolean;
}

export const AttachmentGrid = React.forwardRef<
  HTMLDivElement,
  AttachmentGridProps
>(({ children, count, narrow }, ref) => {
  return (
    <div
      ref={ref}
      data-attachment-count={count}
      className={classNames(styles.attachmentGrid, {
        [styles.narrow]: narrow,
      })}
    >
      {children}
    </div>
  );
});

interface AnimatedAttachmentGridProps {
  children: React.ReactNode;
  shouldExpand: boolean;
  hasOpenMenu: boolean;
  narrow: boolean;
  count: number;
}

const AnimatedAttachmentGrid = ({
  children,
  shouldExpand,
  hasOpenMenu,
  narrow,
  count,
}: AnimatedAttachmentGridProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [lastCompletedAnimation, setLastCompletedAnimation] =
    useState<AnimationDefinition>('collapsed');

  const isAnimating =
    (!shouldExpand && lastCompletedAnimation !== 'collapsed') ||
    (shouldExpand && lastCompletedAnimation !== 'expanded');

  const isExpanded = shouldExpand || isAnimating;
  const gridHeight = useGridHeight(gridRef, shouldExpand);

  useEffect(() => {
    if (containerRef.current) {
      // We want reset any scrolling that happened in the container.
      // To make sure this works in safari, we need to set overflow to hidden
      // before we reset the scroll, only then the scroll will reset successfully.
      containerRef.current.style.overflow = shouldExpand ? 'auto' : 'hidden';
      containerRef.current.scrollTop = 0;
    }
  }, [shouldExpand]);

  return (
    <div className={styles.attachmentAreaContainer}>
      <motion.div
        ref={containerRef}
        initial={false}
        className={classNames('attachment-area', styles.attachmentArea, {
          [styles.isExpanded]: isExpanded,
          [styles.hasOpenMenu]: hasOpenMenu,
        })}
        transition={{ duration: 0.2, type: 'tween' }}
        animate={shouldExpand ? 'expanded' : 'collapsed'}
        variants={{
          expanded: {
            height: gridHeight + BORDER_WIDTH * 2,
          },
          collapsed: {
            height: COLLAPSED_HEIGHT,
          },
        }}
        data-expanded={isExpanded}
        onAnimationComplete={(definition) =>
          setLastCompletedAnimation(definition)
        }
      >
        <AttachmentGrid ref={gridRef} narrow={narrow} count={count}>
          {children}
        </AttachmentGrid>
      </motion.div>
    </div>
  );
};

interface AttachmentContainerProps {
  attachments: AttachmentFile[];
  placeholder: boolean;
  onDeleteAttachment: (source: string) => void;
  isAttachmentUploaded: (attachment: AttachmentFile) => boolean;
  shouldShowUploadingAttachment: (attachment: AttachmentFile) => boolean;
  blot: Attachment;
}

const AttachmentContainer = ({
  attachments,
  placeholder = false,
  onDeleteAttachment,
  isAttachmentUploaded,
  shouldShowUploadingAttachment,
  blot,
}: AttachmentContainerProps) => {
  const downloadAttachments = useDownloadAttachments();
  const [shouldExpand, setShouldExpand] = useState(false);
  useBlotHighlightedClass(shouldExpand || placeholder ? blot : null);

  const readOnly = useQuillReadOnly();
  const visibleAttachments = attachments.filter(
    (attachment) =>
      isAttachmentUploaded(attachment) ||
      shouldShowUploadingAttachment(attachment),
  );
  const downloadableAttachments =
    visibleAttachments.filter(isAttachmentUploaded);

  const hasUploadingAttachments =
    attachments.filter(
      (attachment) =>
        !isAttachmentUploaded(attachment) &&
        shouldShowUploadingAttachment(attachment),
    ).length > 0;

  useEffect(() => {
    if (hasUploadingAttachments) {
      setShouldExpand(true);
    }
  }, [hasUploadingAttachments]);

  const count = visibleAttachments.length;

  const [attachmentWithOpenMenu, setAttachmentWithOpenMenu] =
    useState<AttachmentFile | null>(null);
  const hasOpenMenu = attachmentWithOpenMenu != null;

  const [setElement, isNarrow] = useElementSizeSelector(
    ({ width }) => width < 500,
  );

  const handleDownloadAll =
    downloadableAttachments.length > 0 && downloadAttachments
      ? () => downloadAttachments(downloadableAttachments)
      : null;

  // Other collaborators may be uploading
  if (!count) {
    return null;
  }

  return (
    <ClickOutsideHandler
      ref={setElement}
      className={styles.container}
      onMouseEnter={() => setShouldExpand(true)}
      onMouseLeave={() => setShouldExpand(false)}
      onClickOutside={() => setShouldExpand(false)}
    >
      <div data-no-print className={styles.header}>
        <span className={styles.meta}>
          {count} {inflection.inflect('attachment', count)}
        </span>
      </div>
      <AnimatedAttachmentGrid
        shouldExpand={shouldExpand || hasOpenMenu || placeholder}
        hasOpenMenu={hasOpenMenu}
        narrow={isNarrow ?? false}
        count={count + (placeholder ? 1 : 0)}
      >
        {visibleAttachments.map((attachment) => (
          <AttachmentItem
            key={attachment.source}
            attachment={attachment}
            onOverflowMenuOpenChange={(isOpen) => {
              setAttachmentWithOpenMenu((current) => {
                if (current === attachment) return null;
                return isOpen ? attachment : current;
              });
            }}
            isOverflowMenuOpen={attachmentWithOpenMenu === attachment}
            readOnly={readOnly}
            onDeleteAttachment={() => onDeleteAttachment(attachment.source)}
            isAttachmentUploaded={isAttachmentUploaded}
            onDownloadAll={handleDownloadAll}
            blot={blot}
          />
        ))}
        {placeholder && <div className={styles.placeholder} />}
      </AnimatedAttachmentGrid>
    </ClickOutsideHandler>
  );
};

export default AttachmentContainer;
